import { IterUtils } from "../../../../../Common/IterableUtils";
import { ICandyComponentHandle } from "../../Core/ComponentHandleRegistry/ICandyComponentHandle";
import { BindingPath } from "../../Core/CoreTypes";
import { formComponentDidMountEventPath } from "../../Core/EventManagement/ExternalEvents/ExternalEvents";
import { IForm } from "../../Form/IForm";
import {
    AbsoluteResolvedModelPath,
    createAbsolute,
    createAbsoluteFromTokens,
    createModelPath,
} from "../../../../../Common/ModelPath/ModelPath";
import { isNotNullOrEmpty, isNotNullOrUndefined } from "../../../../../Common/TypingUtils";
import {
    extractInstanceFromBindingPath,
    SectionName,
    sectionNameToUnitId,
    UnitId,
} from "../../../Controls/FormParts/Unit/UnitId/UnitId";
import { SectionNames } from "../../../../../Common/CommonConstants/SectionNames";
import { UnitInfo } from "../../../Controls/UnitInfo";
import { PathUtils } from "../../Core/PathUtils/PathUtils";
import { splitPathWithComma } from "../../Core/ErrorManager/Utils/Utils";
import { openModalUnitFromErrorLogAction } from "../../ActionCreators/UnitActionCreators";

import { IActiveLogHelper } from "./IActiveLogHelper";

export class ActiveLogHelper implements IActiveLogHelper {
    private readonly form: IForm;

    public constructor(form: IForm) {
        this.form = form;
    }

    public async navigate(navigationPath: string): Promise<undefined | ICandyComponentHandle> {
        return this.form.lazyLoadingManager.isLazyLoadingEnabled()
            ? this.navigateByHighlanderPaths(Iterator.from(navigationPath.split(splitPathWithComma)))
            : this.navigateByBindingPaths(
                  Iterator.from(navigationPath.split(splitPathWithComma)).map(x =>
                      this.form.navigationPathMapper.mapPath(x)
                  )
              );
    }

    public async navigateByBindingPaths(paths: Iterable<BindingPath>): Promise<undefined | ICandyComponentHandle> {
        for (const path of paths) {
            await this.findUnitAndScroll(path);
            const result = await this.findControlAndFocus(path);
            if (isNotNullOrUndefined(result)) {
                return result;
            }
        }
        return undefined;
    }

    public async navigateByHighlanderPaths(paths: Iterable<string>): Promise<undefined | ICandyComponentHandle> {
        const bindingPaths = await Promise.all(
            Iterator.from(paths)
                .map(path => this.form.navigationPathMapper.mapPathWithLazyLoading(path))
                .toArray()
        );

        return this.navigateByBindingPaths(bindingPaths);
    }

    public async findShowOnDemandUnit(sectionNames: SectionName[], path: BindingPath): Promise<UnitId | undefined> {
        const unitInfos = this.form.store.getState().unitInfos;
        if (unitInfos == undefined) {
            return undefined;
        }
        for (const sectionName of sectionNames) {
            const pageId = sectionName.split("-")[0];
            if (pageId === undefined || pageId === "default") {
                continue;
            }
            const result = this.findUnitInfoById(unitInfos, { pageId: pageId });
            if (!result?.showOnDemand) {
                continue;
            }
            const unitId = result.multiple
                ? {
                      pageId: pageId,
                      instance: extractInstanceFromBindingPath(
                          createAbsolute(path),
                          {
                              pageId: pageId,
                          },
                          unitInfos
                      ),
                  }
                : {
                      pageId: pageId,
                  };
            await this.navigateToShowOnDemandUnit(unitId, path);
            return unitId;
        }
        return undefined;
    }

    private async navigateToShowOnDemandUnit(unitId: UnitId, path: BindingPath): Promise<void> {
        const unitInfos = this.form.store.getState().unitInfos;
        if (!unitInfos) {
            return;
        }
        const result = this.findUnitInfoById(unitInfos, unitId);
        if (result?.onNavigateFromErrorLog) {
            result.onNavigateFromErrorLog?.(this.form.getHelperFunctionsCallContext(), unitId, path);
        } else {
            this.form.store.dispatch(openModalUnitFromErrorLogAction(unitId, path));
        }
    }

    private async findUnitAndScroll(path: string): Promise<void> {
        const sections = this.form.dataDeclarationHelper.getSections(path);
        let unitId = IterUtils.last(
            sections
                .filter(x => x != SectionNames.defaultSection && x != SectionNames.requisitesSection)
                .map(sectionNameToUnitId)
        );
        if (isNotNullOrUndefined(unitId)) {
            await this.findParentUnitAndScroll(path, unitId);
        }

        unitId = (await this.findShowOnDemandUnit(sections, path)) ?? unitId;

        if (isNotNullOrUndefined(unitId)) {
            await this.form.unitNavigator?.scrollToUnit(unitId);
        }
    }

    private async findParentUnitAndScroll(path: string, unitId: UnitId) {
        if (unitId?.parentInstances?.length) {
            const potentialOtherSectionToken = Array.from(unitId?.parentInstances).shift();
            if (isNotNullOrEmpty(potentialOtherSectionToken)) {
                const splitArray = PathUtils.split(path);
                const otherSectionPath = splitArray.slice(0, splitArray.indexOf(potentialOtherSectionToken) + 1);
                const modelPath = createAbsoluteFromTokens(otherSectionPath).toLegacyPath();
                const potentialOtherSection = this.form.dataDeclarationHelper.getSections(modelPath);

                const parentUnitId = IterUtils.last(
                    potentialOtherSection
                        .filter(x => x != SectionNames.defaultSection && x != SectionNames.requisitesSection)
                        .map(sectionNameToUnitId)
                );
                if (isNotNullOrUndefined(parentUnitId)) {
                    const preparedUnit = this.prepareParentUnitIdToNavigate(parentUnitId, potentialOtherSectionToken);
                    await this.form.unitNavigator?.scrollToUnit(preparedUnit);
                }
            }
        }
    }

    private prepareParentUnitIdToNavigate(unitId: UnitId, sectionInstanceToken: string): UnitId {
        return { pageId: unitId.pageId, instance: unitId.instance ?? sectionInstanceToken };
    }

    private async findControlAndFocus(path: string): Promise<ICandyComponentHandle | undefined> {
        const { multiplicityPath, resultPaths: navigationPaths } = this.getPathParts(path);
        let lastComponent: ICandyComponentHandle | undefined;
        let lastPath: string | undefined;
        for (const [index, currPath] of navigationPaths.entries()) {
            const componentPath = this.cutLastInstanceFromPath(currPath, path);
            let preparedPath = currPath;

            if (preparedPath.startsWith("/")) {
                preparedPath = preparedPath.substring(1);
            }

            if (multiplicityPath && multiplicityPath === preparedPath) {
                await this.navigateByMask(multiplicityPath, path);
            }

            const unitInfos = this.form.store.getState().unitInfos;
            const isUnit = unitInfos && this.findUnitInfoByPath(unitInfos, preparedPath);

            let components = this.form.componentHandleRegistry.getComponentHandles(componentPath);
            if (components == undefined || components.length === 0) {
                await this.waitMountComponent(componentPath);
                components = this.form.componentHandleRegistry.getComponentHandles(componentPath);
            }

            if (
                navigationPaths[navigationPaths.length - 1] === currPath &&
                (components == undefined || components.length === 0)
            ) {
                components = this.form.componentHandleRegistry.getComponentHandles(preparedPath);
            }

            components = [...(components ?? [])];
            if (components.length !== 0) {
                lastComponent = IterUtils.last(components.filter(isNotNullOrUndefined));
                lastPath = currPath;
                for (const component of components) {
                    if (isUnit) {
                        continue;
                    }
                    await component.makePathAvailableForFocus(createModelPath(currPath, true).toAbsolute());
                    component.focus(currPath);
                }
            } else if (lastComponent && lastPath && index === navigationPaths.length - 1) {
                lastComponent.focus(lastPath);
            }
        }

        return lastComponent;
    }

    private async navigateByMask(currentPath: string, navigationPath: string) {
        const componentPath = this.cutLastInstanceFromPath(currentPath, navigationPath);
        let maskPath = this.form.dataDeclarationHelper.getMask(componentPath);
        if (maskPath) {
            maskPath = PathUtils.trimLastStarIfLastToken(maskPath);
            const componentByMask = this.form.componentHandleRegistry.getComponentHandles(maskPath) ?? [];
            await this.waitMountComponent(navigationPath);
            for (const component of componentByMask) {
                await component.makePathAvailableForFocus(createModelPath(currentPath, true).toAbsolute());
                component.focus(currentPath);
            }
        }
    }

    private getPathParts(path: string): { multiplicityPath: string; resultPaths: string[] } {
        const emptyReturn = {
            multiplicityPath: "",
            resultPaths: [],
        };
        const allPartsPath = Iterator.from(createAbsolute(path).getPathParts()).toArray();
        const partsPathBeforeMultiplicity: AbsoluteResolvedModelPath[] = [];

        allPartsPath.forEach((item, index, array) => {
            const itemAbsolute = createAbsolute(item);
            if (index === 0) {
                partsPathBeforeMultiplicity.push(itemAbsolute);
            } else {
                const lastIndex = partsPathBeforeMultiplicity.length - 1;
                if (!isNaN(Number(array[index - 1]))) {
                    partsPathBeforeMultiplicity.push(itemAbsolute);
                } else {
                    const lastItem = partsPathBeforeMultiplicity[lastIndex];
                    if (lastItem !== undefined) {
                        partsPathBeforeMultiplicity[lastIndex] = lastItem.joinWith(item);
                    }
                }
            }
        });

        const multiplicityPathArray = partsPathBeforeMultiplicity;
        const lastMultiplicity = multiplicityPathArray.pop();

        const multiplicityPath = multiplicityPathArray.map(i => i.toLegacyPath()).join("/");
        if (lastMultiplicity == undefined) {
            return emptyReturn;
        }

        const tokensInLastMultiplicity = Iterator.from(lastMultiplicity.getPathParts()).toArray();
        if (tokensInLastMultiplicity == undefined) {
            return emptyReturn;
        }
        const partsPathToBeJoined = partsPathBeforeMultiplicity.concat(
            tokensInLastMultiplicity.map(i => createAbsolute(i))
        );

        let currentJoinedPath = "";
        const resultPaths: string[] = [];
        partsPathToBeJoined.forEach(item => {
            currentJoinedPath = `${currentJoinedPath}${item}`;
            resultPaths.push(currentJoinedPath);
        });

        return {
            multiplicityPath: multiplicityPath,
            resultPaths: resultPaths,
        };
    }

    private cutLastInstanceFromPath(currentPath: string, navigationPath: string): string {
        const match = /([^/]*)$/.exec(currentPath);
        if (match == undefined) {
            throw new Error("Invalid path");
        }
        const componentIndex = Number(match[0]);
        if (currentPath.startsWith("/")) {
            currentPath = currentPath.substring(1);
        }
        const componentPath =
            isNaN(componentIndex) || currentPath === navigationPath
                ? currentPath
                : currentPath.replace(/\/[^/]+$/g, "");
        return componentPath;
    }

    private waitMountComponent(componentPath: string): Promise<void> {
        return new Promise(resolve => {
            const timeout = setTimeout(() => {
                this.form.offExternalEvent(formComponentDidMountEventPath(componentPath), focusEvent);
                resolve();
            }, 100);

            const focusEvent = () => {
                this.form.offExternalEvent(formComponentDidMountEventPath(componentPath), focusEvent);
                clearTimeout(timeout);
                resolve();
            };

            this.form.onExternalEvent(formComponentDidMountEventPath(componentPath), focusEvent);
        });
    }

    private findUnitInfoById(unitInfos: UnitInfo[], unitId: UnitId): UnitInfo | undefined {
        return unitInfos.find(x => x.id === unitId.pageId);
    }

    private findUnitInfoByPath(unitInfos: UnitInfo[], bindingPath: string): UnitInfo | undefined {
        const unitInfo = unitInfos.find(x => x.bindingPath.toLegacyPath() === bindingPath);
        return unitInfo;
    }
}
